/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';

import {
  UniversalNav,
  UniversalNavProvider,
  UNIVERSAL_NAV_ZINDEX,
  useUniversalNavContext,
} from '@zapier/universal-nav';
import { UniversalFooter } from '@zapier/universal-footer';
import { AlertBanner } from '../AlertBanner';
import type { TrackEvent, UseMutation } from '../AlertBanner/types';

type UniversalNavProps = React.ComponentProps<typeof UniversalNav>;
type UniversalNavProviderProps = React.ComponentProps<
  typeof UniversalNavProvider
>;
type UniversalFooterProps = React.ComponentProps<typeof UniversalFooter>;

type Props = {
  /**
   * The current URL of the page. See details in source prop.
   */
  currentUrl: UniversalNavProviderProps['currentUrl'];
  /**
   * The current account `id` that a user is switched to. See details in source prop.
   */
  currentAccountId?: UniversalNavProviderProps['currentAccountId'];
  /**
   * Hook to actually execute the query. Typically provided by an `@apollo` package.
   */
  useQuery: UniversalNavProviderProps['useQuery'];
  /**
   * Hook to actually execute the mutation. Typically provided by an `@apollo` package.
   */
  useMutation: UseMutation;
  /**
   * Function to track user events when they occur.
   */
  trackEvent: TrackEvent;
  /**
   * Function to render notifications in `UniversalTopbar`. See details in source prop.
   */
  renderNotifications?: UniversalNavProps['renderNotifications'];
  /**
   * Whether the alphabetic app nav should render in the footer. See details in source prop.
   */
  shouldRenderAlphabetNav?: UniversalFooterProps['shouldRenderAlphabetNav'];
  /**
   * Whether the SEO links should render in the footer. See details in source prop.
   */
  shouldRenderSeoLinks?: UniversalFooterProps['shouldRenderSeoLinks'];
  /**
   * The main content to render.
   */
  children: React.ReactNode;
  /**
   * The HTML `id` of the main content node. **Important:** this must
   * be set as the `id` on the returned HTML node from `renderMainContent`.
   * It also must be passed as `skipLinkHref` to `UniversalNav` (prefixed
   * with `#`) in order for it to properly function as a skip link.
   */
  mainContentId: string;
  /**
   * Render an optional alert banner above the header / navigation.
   * Use for important notifications.
   */
  renderAlertBanner: (props: Props) => React.ReactNode;
  /**
   * Render the header, which is primarily the universal navigation.
   */
  renderHeader: (props: Props) => React.ReactNode;
  /**
   * Render the footer.
   */
  renderFooter: (props: Props) => React.ReactNode;
};

const Styles = {
  root: css`
    display: block;
  `,
  alertBanner: css`
    &:empty {
      display: none;
    }
  `,
  grid: css`
    min-height: 100vh;
    display: grid;
    grid-template-columns: 100%;
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
      'head'
      'main'
      'foot';
    gap: 0;
    transition: all ease-in-out 300ms;
  `,
  gridWithSidebar: (sidebarWidth: number) => css`
    grid-template-columns: ${sidebarWidth}px 1fr;
    grid-template-areas:
      'head head'
      '.... main'
      '.... foot';
  `,
  head: (topbarHeight: number) => css`
    grid-area: head;
    // Stickiness is handled by UniversalNav but needs
    // to be reinforced here to work properly.
    position: sticky;
    z-index: ${UNIVERSAL_NAV_ZINDEX};
    top: -${topbarHeight}px;
  `,
  main: css`
    grid-area: main;
    position: relative;
  `,
  foot: css`
    grid-area: foot;
  `,
};

const defaultProps = {
  mainContentId: 'mainContent',
  renderAlertBanner: (props: Props) => (
    <AlertBanner
      trackEvent={props.trackEvent}
      useMutation={props.useMutation}
      useQuery={props.useQuery}
    />
  ),
  renderNotifications: () => null,
  renderHeader: (props: Props) => {
    return (
      <UniversalNav
        renderNotifications={props.renderNotifications}
        skipLinkHref={`#${props.mainContentId}`}
      />
    );
  },
  renderFooter: (props: Props) => {
    return (
      <UniversalFooter
        shouldRenderAlphabetNav={props.shouldRenderAlphabetNav}
        shouldRenderSeoLinks={props.shouldRenderSeoLinks}
      />
    );
  },
};

/**
 * Inner component to be able to `useUniversalNavContext`.
 */
const UniversalLayoutInner = (props: Props) => {
  const { sidebarWidth, topbarHeight } = useUniversalNavContext();
  return (
    <div css={Styles.root}>
      <div css={Styles.alertBanner} data-testid="UniversalLayoutAlertBanner">
        {props.renderAlertBanner(props)}
      </div>
      <div
        css={[
          Styles.grid,
          sidebarWidth && Styles.gridWithSidebar(sidebarWidth),
        ]}
      >
        <div css={Styles.head(topbarHeight)}>{props.renderHeader(props)}</div>
        <main css={Styles.main} id={props.mainContentId}>
          {props.children}
        </main>
        <div css={Styles.foot}>{props.renderFooter(props)}</div>
      </div>
    </div>
  );
};

/**
 * The universal page layout component that renders the main navigation and footer.
 */
export const UniversalLayout = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  return (
    <UniversalNavProvider
      currentAccountId={props.currentAccountId}
      currentUrl={props.currentUrl}
      useQuery={props.useQuery}
    >
      <UniversalLayoutInner {...props} />
    </UniversalNavProvider>
  );
};

UniversalLayout.defaultProps = defaultProps;
